import React, { FC, useEffect, useState, useMemo, startTransition, useCallback } from 'react';
import {
  Button,
  IconButton,
  Box,
  CircularProgress,
  Typography,
  Chip,
  Grid,
  Card,
  CardContent,
  Avatar,
  InputBase,
  Divider,
  Pagination,
  alpha,
  MenuItem,
  Select,
  FormControl,
} from '@mui/material';
import {
  Add,
  Search,
  GridView,
  ViewList,
  LocationOn,
  CalendarToday,
  OpenInNew,
  Sensors,
  RocketLaunch,
  PauseCircle,
  Inventory2,
} from '@mui/icons-material';
import { useAppState } from 'src/state-management/state-provider';
import { AdminAction } from 'src/state-management/admin.action';
import {
  DeviceAdminDetail,
  DeviceAdminDetailWithDeployment,
  DeploymentAdminListDetail,
  ProjectAdminSummary,
} from 'src/api/Api';
import clientService from 'src/clientService';

import {
  getDeviceTypes,
  getDeviceStatuses,
  getDeviceConditions,
  DeviceTypeResponse,
} from 'src/api2/deviceCustodyApi';

import ConfirmationDialog from 'src/view/components/ConfirmationDialog';
import { useSnackbar } from 'src/view/components/SnackbarService';
import { formatDate } from 'src/util/date-utils';

// Import local images for devices
import audioImg from 'src/resources/images/audio.png';
import cameratrapImg from 'src/resources/images/cameratrap.png';
import hydroImg from 'src/resources/images/hydro.png';

// --- Sub-components (Dialogs) ---
import AddDeviceDialog from 'src/view/Admin/Devices/AddDevicePage';
import EditDeviceDialog from 'src/view/Admin/Devices/EditDevicePage';

// --- Helpers ---

const STATUS_COLOR: Record<string, string> = {
  // Operational Statuses
  DEPLOYED: '#00E676',
  IDLE_AT_PROJECT: '#FFD600',
  IN_INVENTORY: '#29B6F6',
  RETRIEVED: '#FF9800',

  // Conditions
  NORMAL: '#00E676',
  DAMAGED: '#FF5252',
  MAINTENANCE: '#FFD600',
  MISSING: '#FF5252',
  RETIRED: '#666666',

  // Fallbacks
  ACTIVE: '#00E676',
  INACTIVE: '#FF5252',
  DEFECTIVE: '#FF5252',
};

const getStatusColor = (status: string): string => {
  return STATUS_COLOR[status?.toUpperCase()] || '#AAAAAA';
};

const getStatusLabel = (status: string): string => {
  switch (status?.toUpperCase()) {
    case 'DEPLOYED': return 'Deployed';
    case 'IDLE_AT_PROJECT': return 'Idle at Project';
    case 'IN_INVENTORY': return 'In Inventory';
    case 'RETRIEVED': return 'Retrieved';
    default: return status;
  }
};

const getConditionLabel = (condition: string): string => {
  switch (condition?.toUpperCase()) {
    case 'NORMAL': return 'Normal';
    case 'DAMAGED': return 'Damaged';
    case 'MAINTENANCE': return 'Maintenance';
    case 'MISSING': return 'Missing';
    case 'RETIRED': return 'Retired';
    default: return condition;
  }
};

/** Resolve device type category from either the new relation or the old type field */
const getDeviceCategory = (device: DeviceAdminDetailWithDeployment): string => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (device as any)?.device_type?.category ?? (device as any)?.device_type?.name ?? device.type ?? '—';
};

/** Get the corresponding image based on device category */
const getDeviceImage = (category: string, deviceId: string): string => {
  switch (category?.toUpperCase()) {
    case 'AUDIO':
      return audioImg;
    case 'CAMERA':
    case 'CAMERATRAP':
    case 'IMAGING':
      return cameratrapImg;
    case 'HYDRO':
    case 'HYDROLOGY':
      return hydroImg;
    default:
      return `https://api.dicebear.com/7.x/shapes/svg?seed=${deviceId}`;
  }
};

// --- Sub-components ---

const StatCard: FC<{ icon: React.ReactNode; label: string; value: string | number; color: string }> = ({
  icon, label, value, color
}) => (
  <Card sx={{
    bgcolor: 'rgba(28, 28, 28, 0.4)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.05)',
    borderRadius: 3,
    minWidth: 200,
    flex: 1
  }}>
    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: '20px !important' }}>
      <Avatar sx={{ bgcolor: alpha(color, 0.1), color: color, width: 48, height: 48 }}>
        {icon}
      </Avatar>
      <Box>
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)', mb: 0.5 }}>
          {label}
        </Typography>
        <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700 }}>
          {value.toLocaleString()}
        </Typography>
      </Box>
    </CardContent>
  </Card>
);

// --- Custody/Deployment location display ---

const DeviceLocationInfo: FC<{
  device: DeviceAdminDetailWithDeployment;
  deployment?: DeploymentAdminListDetail;
  project?: ProjectAdminSummary;
}> = ({ device, deployment, project }) => {
  if (deployment) {
    return (
      <>
        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.3)', fontWeight: 700, display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Deployed At
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600 }}>
            {deployment.site_name ?? '—'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
          <LocationOn sx={{ color: 'rgba(255, 255, 255, 0.3)', fontSize: '14px' }} />
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.3)' }}>
            {deployment.location_name ? `${deployment.location_name}, ${deployment.project_name}` : deployment.project_name ?? '—'}
          </Typography>
        </Box>
      </>
    );
  }

  if (device.assigned_project_id && project) {
    return (
      <>
        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.3)', fontWeight: 700, display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Project Custody
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600 }}>
            {project.project_name}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
          <Inventory2 sx={{ color: 'rgba(255, 255, 255, 0.3)', fontSize: '14px' }} />
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.3)' }}>
            Checked out to project
          </Typography>
        </Box>
      </>
    );
  }

  return (
    <>
      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.3)', fontWeight: 700, display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        Stock Status
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600 }}>
          In Stock
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
        <LocationOn sx={{ color: 'rgba(255, 255, 255, 0.3)', fontSize: '14px' }} />
        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.3)' }}>
          Available in general stock
        </Typography>
      </Box>
    </>
  );
};


const DeviceGridCard: FC<{
  device: DeviceAdminDetailWithDeployment;
  deployment?: DeploymentAdminListDetail;
  project?: ProjectAdminSummary;
  onEdit: (d: DeviceAdminDetail) => void;
  onDelete: (id: string) => void;
}> = ({ device, deployment, project, onEdit }) => {
  const statusLabel = (device as any).operational_status || device.status;
  const statusColor = getStatusColor(statusLabel);
  const deviceCategory = getDeviceCategory(device);

  return (
    <Card sx={{
      bgcolor: 'rgba(28, 28, 28, 0.6)',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: 4,
      position: 'relative',
      transition: 'transform 0.2s, boxShadow 0.2s',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
      }
    }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip
              label={getStatusLabel(statusLabel)}
              size="small"
              sx={{
                bgcolor: alpha(statusColor, 0.2),
                color: statusColor,
                fontWeight: 700,
                fontSize: '10px',
                borderRadius: '4px'
              }}
            />
            {device.condition && (
              <Chip
                label={getConditionLabel(device.condition)}
                size="small"
                sx={{
                  bgcolor: alpha(getStatusColor(device.condition), 0.1),
                  color: getStatusColor(device.condition),
                  fontWeight: 700,
                  fontSize: '10px',
                  borderRadius: '4px',
                  border: `1px solid ${alpha(getStatusColor(device.condition), 0.2)}`
                }}
              />
            )}
          </Box>
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.3)', fontWeight: 600 }}>
            ID: {device.human_readable_id || device.asset_tag || device.serial_number || '—'}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Box sx={{
            width: 80,
            height: 80,
            bgcolor: 'rgba(255, 255, 255, 0.05)',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden'
          }}>
            <img
              src={getDeviceImage(deviceCategory, device.device_id)}
              alt="Device"
              style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.9 }}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, lineHeight: 1.2, mb: 0.5 }}>
              {device.name || device.human_readable_id}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)', mb: 0.5 }}>
              {deviceCategory}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.3)', display: 'block' }}>
              SN: {device.serial_number}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.05)', mb: 2 }} />

        <Box sx={{ mb: 2 }}>
          <DeviceLocationInfo device={device} deployment={deployment} project={project} />
        </Box>

        <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.05)', mb: 2 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.3)', fontWeight: 700, display: 'block', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Purchased At
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CalendarToday sx={{ color: 'rgba(255, 255, 255, 0.3)', fontSize: '14px' }} />
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontWeight: 600 }}>
                {formatDate(device.purchased_at)}
              </Typography>
            </Box>
          </Box>
          <IconButton
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.05)',
              color: '#fff',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' }
            }}
            onClick={() => onEdit(device)}
          >
            <OpenInNew fontSize="small" />
          </IconButton>
        </Box>
      </CardContent>
    </Card>
  );
};

const DeviceListRow: FC<{
  device: DeviceAdminDetailWithDeployment;
  deployment?: DeploymentAdminListDetail;
  project?: ProjectAdminSummary;
  onEdit: (d: DeviceAdminDetail) => void;
  onDelete: (id: string) => void;
}> = ({ device, deployment, project, onEdit }) => {
  const statusLabel = (device as any).operational_status || device.status;
  const statusColor = getStatusColor(statusLabel);
  const deviceCategory = getDeviceCategory(device);

  // Determine location text for list view
  let locationMain = '-';
  let locationSub = 'No deployment';
  if (deployment) {
    locationMain = deployment.site_name ?? '—';
    locationSub = deployment.project_name ?? '—';
  } else if (device.assigned_project_id && project) {
    locationMain = project.project_name;
    locationSub = 'Project custody';
  } else {
    locationMain = 'In Stock';
    locationSub = 'General stock';
  }

  return (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: '1.5fr 1fr 1fr 1.2fr 1fr 100px 60px',
      alignItems: 'center',
      py: 2,
      px: 3,
      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
      '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.02)' }
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar src={getDeviceImage(deviceCategory, device.device_id)} sx={{ width: 32, height: 32, bgcolor: 'rgba(255,255,255,0.05)' }} />
        <Box>
          <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600 }}>{device.name || device.human_readable_id}</Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)' }}>ID: {device.human_readable_id || device.asset_tag || device.serial_number || '—'}</Typography>
        </Box>
      </Box>
      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>{deviceCategory}</Typography>
      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>{device.serial_number}</Typography>
      <Box>
        <Typography variant="body2" sx={{ color: '#fff' }}>{locationMain}</Typography>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)' }}>{locationSub}</Typography>
      </Box>
      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>{formatDate(device.purchased_at)}</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Chip
          label={getStatusLabel(statusLabel)}
          size="small"
          sx={{
            bgcolor: alpha(statusColor, 0.1),
            color: statusColor,
            fontWeight: 700,
            fontSize: '9px',
            borderRadius: '4px',
            height: '18px',
            width: 'fit-content'
          }}
        />
        {device.condition && (
          <Chip
            label={getConditionLabel(device.condition)}
            size="small"
            sx={{
              bgcolor: alpha(getStatusColor(device.condition), 0.1),
              color: getStatusColor(device.condition),
              fontWeight: 700,
              fontSize: '9px',
              borderRadius: '4px',
              height: '18px',
              width: 'fit-content',
              border: `1px solid ${alpha(getStatusColor(device.condition), 0.2)}`
            }}
          />
        )}
      </Box>
      <IconButton size="small" onClick={() => onEdit(device)} sx={{ color: 'rgba(255,255,255,0.3)', '&:hover': { color: '#00E676' } }}>
        <OpenInNew fontSize="small" />
      </IconButton>
    </Box>
  );
};

// --- Main Component ---

const Devices: FC = () => {
  const { state, dispatch } = useAppState();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
  const { showSnackbar, snackbarType } = useSnackbar();

  // Dialog States
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<DeviceAdminDetail | null>(null);

  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [typeFilter, setTypeFilter] = useState('All');
  const [conditionFilter, setConditionFilter] = useState('All');
  const [page, setPage] = useState(1);
  const itemsPerPage = 9;
  const [deviceTypes, setDeviceTypes] = useState<DeviceTypeResponse[]>([]);
  const [deviceStatuses, setDeviceStatuses] = useState<string[]>([]);
  const [deviceConditions, setDeviceConditions] = useState<string[]>([]);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const [types, statuses, conditions] = await Promise.all([
          getDeviceTypes(),
          getDeviceStatuses(),
          getDeviceConditions(),
        ]);
        setDeviceTypes(types);
        setDeviceStatuses(statuses);
        setDeviceConditions(conditions);
      } catch (err) {
        console.error('Failed to fetch device metadata types/statuses/conditions:', err);
      }
    };
    fetchConfig();
  }, []);

  // Cast to the richer type that includes deployment_id and assigned_project_id
  const devices = state.admin.devices as DeviceAdminDetailWithDeployment[];

  // Data fetching — fetch devices, deployments, and projects together
  useEffect(() => {
    const fetchData = async () => {
      if (state.admin.devices.length === 0 || state.admin.deployments.length === 0) {
        setLoading(true);
        setError(null);
        try {
          const [devicesRes, deploymentsRes, projectsRes] = await Promise.all([
            clientService.api.getAllDevicesAdminApiV1AdminDevicesGet(),
            clientService.api.getAllDeploymentsApiV1AdminDeploymentsGet(),
            clientService.api.getAllProjectsAdminApiV1AdminProjectsGet(),
          ]);

          startTransition(() => {
            dispatch({ type: 'ADMIN_SET_DEVICES', payload: devicesRes.data } as AdminAction);
            dispatch({ type: 'ADMIN_SET_DEPLOYMENT_LIST', payload: Array.isArray(deploymentsRes.data) ? (deploymentsRes.data as any) : ((deploymentsRes.data as any)?.deployments || []) } as AdminAction);
            dispatch({ type: 'ADMIN_SET_PROJECTS', payload: projectsRes.data } as AdminAction);
          });
        } catch (err: unknown) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to fetch data';
          setError(errorMessage);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchData();
  }, [state.admin.devices.length, state.admin.deployments.length, dispatch]);

  const handleAddDevice = () => {
    setAddDialogOpen(true);
  };

  const handleEditDevice = useCallback((device: DeviceAdminDetail) => {
    setSelectedDevice(device);
    setEditDialogOpen(true);
  }, []);

  const handleAddSuccess = (newDevice: DeviceAdminDetail) => {
    const updatedDevices = [...state.admin.devices, newDevice];
    dispatch({ type: 'ADMIN_SET_DEVICES', payload: updatedDevices } as AdminAction);
  };

  const handleEditSuccess = (updatedDevice: DeviceAdminDetail) => {
    const updatedDevices = state.admin.devices.map(d =>
      d.device_id === updatedDevice.device_id ? updatedDevice : d
    );
    dispatch({ type: 'ADMIN_SET_DEVICES', payload: updatedDevices } as AdminAction);
  };

  const handleDeleteDevice = useCallback((deviceId: string) => {
    setSelectedDeviceId(deviceId);
    setConfirmOpen(true);
  }, []);

  const handleConfirmDelete = async () => {
    if (selectedDeviceId) {
      try {
        await clientService.api.deleteApiDeviceApiV1AdminDevicesDeviceIdDelete(selectedDeviceId);
        const updatedDevices = state.admin.devices.filter(
          (device) => device.device_id !== selectedDeviceId
        );
        dispatch({ type: 'ADMIN_SET_DEVICES', payload: updatedDevices });
        showSnackbar({ message: 'Device retired successfully', type: snackbarType.SUCCESS });
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to retire device';
        setError(errorMessage);
        showSnackbar({ message: 'Failed to retire device', type: snackbarType.ERROR });
      } finally {
        setConfirmOpen(false);
        setSelectedDeviceId(null);
      }
    }
  };

  // Map: deployment_id -> Deployment Details (for deployed devices)
  const deploymentByIdMap = useMemo(() => {
    const map = new Map<string, DeploymentAdminListDetail>();
    state.admin.deployments.forEach(d => {
      if (d.deployment_id) map.set(d.deployment_id, d);
    });
    return map;
  }, [state.admin.deployments]);

  // Map: project_id -> ProjectAdminSummary (for project-custody devices)
  const projectMap = useMemo(() => {
    const map = new Map<string, ProjectAdminSummary>();
    state.admin.projects.forEach(p => {
      if (p.project_id) map.set(p.project_id, p);
    });
    return map;
  }, [state.admin.projects]);

  // Derive deployment for a given device using its deployment_id
  const getDeploymentForDevice = useCallback(
    (device: DeviceAdminDetailWithDeployment): DeploymentAdminListDetail | undefined => {
      if (!device.deployment_id) return undefined;
      return deploymentByIdMap.get(device.deployment_id);
    },
    [deploymentByIdMap],
  );

  // Derive project for a given device using its assigned_project_id
  const getProjectForDevice = useCallback(
    (device: DeviceAdminDetailWithDeployment): ProjectAdminSummary | undefined => {
      if (!device.assigned_project_id) return undefined;
      return projectMap.get(device.assigned_project_id);
    },
    [projectMap],
  );

  // Logic: Stats derived from operational_status (computed at read time)
  const stats = useMemo(() => {
    const total = devices.length;
    const deployed = devices.filter(d => (d as any).operational_status === 'DEPLOYED').length;
    const idle = devices.filter(d => (d as any).operational_status === 'IDLE_AT_PROJECT').length;
    const inventory = devices.filter(d => (d as any).operational_status === 'IN_INVENTORY').length;
    return { total, deployed, idle, inventory };
  }, [devices]);

  // Logic: Filtering
  const filteredDevices = useMemo(() => {
    return devices.filter(device => {
      const matchesSearch =
        device.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (device as any).human_readable_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (device as any).serial_number?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStatus = statusFilter === 'All' || (device as any).operational_status === statusFilter;
      const matchesType = typeFilter === 'All' || device.device_type?.device_type_id === typeFilter;
      const matchesCondition = conditionFilter === 'All' || device.condition === conditionFilter;

      return matchesSearch && matchesStatus && matchesType && matchesCondition;
    });
  }, [devices, searchQuery, statusFilter, typeFilter, conditionFilter]);

  // Logic: Pagination
  const paginatedDevices = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    return filteredDevices.slice(start, start + itemsPerPage);
  }, [filteredDevices, page]);

  const totalPages = Math.ceil(filteredDevices.length / itemsPerPage);

  return (
    <Box sx={{
      bgcolor: '#0D0D0D',
      minHeight: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
      p: 4,
      borderRadius: '16px'
    }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h3" sx={{ color: '#fff', fontWeight: 800, mb: 1 }}>
            Devices
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
            Monitor and manage environmental devices in real time.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddDevice}
          sx={{
            bgcolor: '#10B981',
            color: '#FFF',
            fontWeight: 700,
            fontSize: '15px',
            px: 4,
            py: 1.5,
            borderRadius: '14px',
            textTransform: 'none',
            boxShadow: '0 4px 14px 0 rgba(16, 185, 129, 0.39)',
            '&:hover': {
              bgcolor: '#059669',
              boxShadow: '0 6px 20px rgba(5, 150, 105, 0.23)',
            }
          }}
        >
          New Device
        </Button>
      </Box>

      {/* Stats Section */}
      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        <StatCard icon={<Sensors />} label="Total Devices" value={stats.total} color="#00E676" />
        <StatCard icon={<RocketLaunch />} label="Deployed" value={stats.deployed} color="#00E676" />
        <StatCard icon={<PauseCircle />} label="Idle" value={stats.idle} color="#FFD600" />
        <StatCard icon={<Inventory2 />} label="Inventory" value={stats.inventory} color="#FF5252" />
      </Box>

      {/* Filters Section */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        bgcolor: 'rgba(28, 28, 28, 0.4)',
        p: 2,
        borderRadius: '12px',
        border: '1px solid rgba(255, 255, 255, 0.05)'
      }}>
        <Box sx={{ display: 'flex', gap: 2, flex: 1 }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            bgcolor: '#1A1A1A',
            borderRadius: '8px',
            px: 2,
            width: 320,
            border: '1px solid rgba(255,255,255,0.05)'
          }}>
            <Search sx={{ color: 'rgba(255, 255, 255, 0.3)', mr: 1 }} />
            <InputBase
              placeholder="Search devices..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ color: '#fff', width: '100%', fontSize: '14px', py: 0.5 }}
            />
          </Box>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              displayEmpty
              MenuProps={{
                PaperProps: {
                  sx: { bgcolor: '#1A1A1A', color: '#FFF', border: '1px solid #333' }
                }
              }}
              sx={{
                bgcolor: '#1A1A1A',
                color: '#fff',
                borderRadius: '8px',
                border: '1px solid rgba(255,255,255,0.05)',
                '.MuiOutlinedInput-notchedOutline': { border: 'none' },
                fontSize: '14px'
              }}
            >
              <MenuItem value="All">All Status</MenuItem>
              {deviceStatuses.map(status => (
                <MenuItem key={status} value={status}>{getStatusLabel(status)}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              displayEmpty
              MenuProps={{
                PaperProps: {
                  sx: { bgcolor: '#1A1A1A', color: '#FFF', border: '1px solid #333' }
                }
              }}
              sx={{
                bgcolor: '#1A1A1A',
                color: '#fff',
                borderRadius: '8px',
                border: '1px solid rgba(255,255,255,0.05)',
                '.MuiOutlinedInput-notchedOutline': { border: 'none' },
                fontSize: '14px'
              }}
            >
              <MenuItem value="All">All Types</MenuItem>
              {deviceTypes.map(type => (
                <MenuItem key={type.device_type_id} value={type.device_type_id}>{type.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select
              value={conditionFilter}
              onChange={(e) => setConditionFilter(e.target.value)}
              displayEmpty
              MenuProps={{
                PaperProps: {
                  sx: { bgcolor: '#1A1A1A', color: '#FFF', border: '1px solid #333' }
                }
              }}
              sx={{
                bgcolor: '#1A1A1A',
                color: '#fff',
                borderRadius: '8px',
                border: '1px solid rgba(255,255,255,0.05)',
                '.MuiOutlinedInput-notchedOutline': { border: 'none' },
                fontSize: '14px'
              }}
            >
              <MenuItem value="All">All Condition</MenuItem>
              {deviceConditions.map(cond => (
                <MenuItem key={cond} value={cond}>{getConditionLabel(cond)}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ display: 'flex', bgcolor: 'rgba(255, 255, 255, 0.05)', p: 0.5, borderRadius: '8px' }}>
          <IconButton
            size="small"
            onClick={() => setViewMode('grid')}
            sx={{
              color: viewMode === 'grid' ? '#00E676' : 'rgba(255, 255, 255, 0.3)',
              bgcolor: viewMode === 'grid' ? 'rgba(0, 230, 118, 0.1)' : 'transparent',
              borderRadius: '6px'
            }}
          >
            <GridView fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => setViewMode('list')}
            sx={{
              color: viewMode === 'list' ? '#00E676' : 'rgba(255, 255, 255, 0.3)',
              bgcolor: viewMode === 'list' ? 'rgba(0, 230, 118, 0.1)' : 'transparent',
              borderRadius: '6px'
            }}
          >
            <ViewList fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Grid/List Content */}
      <Box>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: '#00E676' }} />
          </Box>
        ) : error ? (
          <Typography variant="body2" sx={{ color: '#FF5252', textAlign: 'center', py: 4 }}>
            {error}
          </Typography>
        ) : filteredDevices.length === 0 ? (
          <Typography sx={{ color: 'rgba(255, 255, 255, 0.3)', textAlign: 'center', py: 8 }}>
            No devices found matching your criteria.
          </Typography>
        ) : viewMode === 'grid' ? (
          <Grid container spacing={3}>
            {paginatedDevices.map(device => (
              <Grid item xs={12} sm={6} md={4} key={device.device_id}>
                <DeviceGridCard
                  device={device}
                  deployment={getDeploymentForDevice(device)}
                  project={getProjectForDevice(device)}
                  onEdit={handleEditDevice}
                  onDelete={handleDeleteDevice}
                />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box sx={{
            bgcolor: 'rgba(28, 28, 28, 0.4)',
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.05)',
            overflow: 'hidden'
          }}>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: '1.5fr 1fr 1fr 1.2fr 1fr 100px 60px',
              py: 1.5,
              px: 3,
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              bgcolor: 'rgba(255, 255, 255, 0.02)'
            }}>
              {['DEVICE', 'TYPE', 'SERIAL NUMBER', 'LOCATION / PROJECT', 'PURCHASED', 'STATUS', ''].map((h, i) => (
                <Typography key={h} variant="caption" sx={{ color: i === 0 ? '#00E676' : 'rgba(255, 255, 255, 0.3)', fontWeight: 700, letterSpacing: '1px' }}>
                  {h}
                </Typography>
              ))}
            </Box>
            {paginatedDevices.map(device => (
              <DeviceListRow
                key={device.device_id}
                device={device}
                deployment={getDeploymentForDevice(device)}
                project={getProjectForDevice(device)}
                onEdit={handleEditDevice}
                onDelete={handleDeleteDevice}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* Pagination Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.3)' }}>
          Showing {(page - 1) * itemsPerPage + 1} to {Math.min(page * itemsPerPage, filteredDevices.length)} of {filteredDevices.length} devices
        </Typography>
        <Pagination
          count={totalPages}
          page={page}
          onChange={(_, p) => setPage(p)}
          sx={{
            '& .MuiPaginationItem-root': {
              color: 'rgba(255, 255, 255, 0.5)',
              borderColor: 'rgba(255, 255, 255, 0.1)',
            },
            '& .MuiPaginationItem-page.Mui-selected': {
              bgcolor: 'rgba(0, 230, 118, 0.2)',
              color: '#00E676',
              borderColor: '#00E676',
            },
            '& .MuiPaginationItem-ellipsis': {
              color: 'rgba(255, 255, 255, 0.2)',
            }
          }}
        />
      </Box>

      {/* Dialog Components */}
      <AddDeviceDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSuccess={handleAddSuccess}
        onDeviceTypeCreated={(newType) =>
          setDeviceTypes(prev => [newType, ...prev])
        }
        deviceTypes={deviceTypes}
        deviceConditions={deviceConditions}
      />

      <EditDeviceDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedDevice(null);
        }}
        device={selectedDevice}
        onSuccess={handleEditSuccess}
        deviceTypes={deviceTypes}
        deviceConditions={deviceConditions}
      />

      <ConfirmationDialog
        open={confirmOpen}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmOpen(false)}
        title="Retire Device"
        content="This will not delete the device but will retire it. Once retired, it cannot be used for any deployment."
        confirmButtonText="Retire"
      />
    </Box>
  );
};

export default Devices;
